import { HttpErrorResponse } from '@angular/common/http';
import { ListFilterGroup } from '@bazis/list/models/list.types';

export const fileDownloaderFn = (fileUrl: string, fileName: string) => {
    const element = document.createElement('a');
    element.setAttribute('href', fileUrl);
    element.setAttribute('download', fileName);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
};

export function getFilenameFromContentDisposition(cd?: string | null): string | null {
    if (!cd) return null;

    const filename = /filename\s*=\s*([^;]+)/i.exec(cd);
    if (filename) return filename[1].trim();

    return null;
}

export const copyMessageFn = (val: string) => {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    window.navigator.clipboard.writeText(val);
    document.body.removeChild(selBox);
};

export const buildFilterStr = (
    params: any,
    paramsSettings: {
        defaultConnector?: string;
        defaultGroupConnector?: string;
        filterGroups?: ListFilterGroup[];
        individualSettings?: { [index: string]: { groupConnector: string } };
    } = null,
) => {
    const defaultConnector = paramsSettings?.defaultConnector || '&';
    const defaultGroupConnector =
        paramsSettings?.defaultGroupConnector || (defaultConnector === '&' ? '|' : '&');
    const filterGroups = paramsSettings?.filterGroups || [];
    const individualSettings = paramsSettings?.individualSettings || {};

    const buildParamsArrayItem = (key, connector): string => {
        if (params[key].length === 1) {
            return `${key}=${params[key][0]}`;
        } else {
            const paramsKeyArr = [];
            params[key].forEach((arrayItem) => {
                paramsKeyArr.push(`${key}=${arrayItem}`);
            });
            const paramGroupConnector = connector || defaultGroupConnector;
            return `(${paramsKeyArr.join(paramGroupConnector)})`;
        }
    };

    const paramsMap: any = {};
    for (let key in params) {
        if (Array.isArray(params[key])) {
            paramsMap[key] = buildParamsArrayItem(key, individualSettings[key]?.groupConnector);
        } else {
            paramsMap[key] = `${key}=${params[key]}`;
        }
    }

    const partOfGroup = {};
    const buildGroup = (group) => {
        const groupArr = [];
        group.group.forEach((groupItemKey) => {
            if (
                group.individualSettings &&
                group.individualSettings[groupItemKey] &&
                Array.isArray(params[groupItemKey])
            ) {
                groupArr.push(
                    buildParamsArrayItem(
                        groupItemKey,
                        group.individualSettings[groupItemKey].groupConnector,
                    ),
                );
            } else {
                groupArr.push(paramsMap[groupItemKey]);
            }
            partOfGroup[groupItemKey] = true;
        });

        if (group.filterGroups) {
            group.filterGroups.forEach((innerGroup) => {
                groupArr.push(buildGroup(innerGroup));
            });
        }

        return `(${groupArr.join(group.groupConnector || defaultGroupConnector)})`;
    };

    const paramsArr = [];

    filterGroups.forEach((group) => {
        paramsArr.push(buildGroup(group));
    });

    for (let key in paramsMap) {
        if (!partOfGroup[key]) paramsArr.push(paramsMap[key]);
    }

    return paramsArr.join(defaultConnector);
};

export const gatherBulkErrors = (error) => {
    if (error instanceof HttpErrorResponse) {
        if (Array.isArray(error.error)) {
            return new HttpErrorResponse({
                status: 422,
                error: {
                    errors: error.error
                        .filter((v) => v.endpoint)
                        .map(
                            (v) =>
                                v.response?.errors?.map((error) => ({
                                    ...error,
                                    endpoint: v.endpoint,
                                })) || [],
                        )
                        .reduce((acc, current) => acc.concat(current), []),
                },
            });
        }
    }
    return null;
};

export const highlightText = (
    initialText: string,
    textToHighlight: string,
    tag: string = 'b',
    tagClass: string = '',
) => {
    return initialText.replace(
        new RegExp(textToHighlight + '(?!([^<]+)?<)', 'gi'),
        `<${tag} class="${tagClass}">$&</${tag}>`,
    );
};

export const flipObject = (object) => {
    return Object.keys(object).reduce((acc, current) => {
        acc[object[current]] = current;
        return acc;
    }, {});
};

export const mathFloatRound = (floatNum: number, toRound?: number) => {
    if (floatNum === 0 || floatNum === null) return floatNum;
    const floatNumStr = floatNum.toString();
    const separator =
        floatNumStr.indexOf(',') !== -1 ? ',' : floatNumStr.indexOf('.') !== -1 ? '.' : undefined;

    if (!separator) {
        if (toRound && toRound !== 0) {
            return parseFloat(floatNum.toFixed(toRound));
        } else return floatNum;
    } else {
        if (toRound && toRound === 0) {
            return Math.round(floatNum);
        } else {
            let finishNum: number;
            // создаем массив из целой и дробной части
            const floatArr = floatNumStr.split(separator);
            // последний символ до округления
            const lastNumStr: string = floatArr[1].charAt(toRound);

            // округление вверх
            if (Number(lastNumStr) === 5) {
                // создаем строковое число, равное одной части разрядности,
                // для округления числа вверх
                let onePartOfRankStrArr = ['0', separator];
                for (let index = 1; index < toRound; index++) {
                    onePartOfRankStrArr.push('0');
                }
                onePartOfRankStrArr.push('1');
                const ceil = Number(onePartOfRankStrArr.join(''));

                // отсекаем часть после плавающей точки, до необходимой разрядности
                const intNumStr: string = floatArr[1].slice(0, toRound);
                floatArr[1] = intNumStr;
                // прибавляем одну часть разряда
                finishNum = Number(floatArr.join(separator)) + ceil;
            } else {
                finishNum = floatNum;
            }

            return parseFloat(finishNum.toFixed(toRound));
        }
    }
};

export const floatMultiply = (num1: number, capacity1: number, num2: number, capacity2: number) => {
    const result = (num1 * capacity1 * num2 * capacity2) / (capacity1 * capacity2);
    return result;
};

export const convertMbToBytes = (value) => {
    return value * 1048576;
};
export const convertBytesToMb = (value) => {
    return value / 1048576;
};

export const latinizeStr = (str: string): string => {
    const RUS_LAT_PAIRS = {
        А: 'A',
        В: 'B',
        Е: 'E',
        К: 'K',
        М: 'M',
        Н: 'H',
        О: 'O',
        Р: 'P',
        С: 'C',
        Т: 'T',
        У: 'Y',
        Х: 'X',
    };
    return [...str].map((v) => RUS_LAT_PAIRS[v] || v).join('');
};

export const isWebgl2Supported = (): boolean => {
    const canvas = document.createElement('canvas');
    const webgl2Context = canvas.getContext('webgl2');
    let supported = true;

    if (!webgl2Context) {
        supported = false;

        return supported;
    }

    const params = [
        { pname: 'MAX_3D_TEXTURE_SIZE', min: 256 },
        { pname: 'MAX_DRAW_BUFFERS', min: 4 },
        { pname: 'MAX_COLOR_ATTACHMENTS', min: 4 },
        { pname: 'MAX_VERTEX_UNIFORM_BLOCKS', min: 12 },
        { pname: 'MAX_VERTEX_TEXTURE_IMAGE_UNITS', min: 16 },
        { pname: 'MAX_FRAGMENT_INPUT_COMPONENTS', min: 60 },
        { pname: 'MAX_UNIFORM_BUFFER_BINDINGS', min: 24 },
        { pname: 'MAX_COMBINED_UNIFORM_BLOCKS', min: 24 },
    ];

    for (let i = 0; i < params.length; i += 1) {
        const param = params[i];
        const value = webgl2Context.getParameter(webgl2Context[param.pname]);

        if (typeof value !== 'number' || Number.isNaN(value) || value < param.min) {
            supported = false;

            break;
        }
    }

    return supported;
};

export const isLocal = (): boolean => {
    return (
        document.location.host.indexOf('localhost') > -1 || document.location.protocol === 'http:'
    );
};

export const processWidgetParams = (params, entities = {}, tools = {}, calculatedValues = {}) => {
    return Object.keys(params).reduce((acc, current) => {
        let processedValue = params[current].value || '';
        if (params[current].isCalculated && calculatedValues[params[current].propertySource]) {
            processedValue = calculatedValues[params[current].propertySource];
        }
        if (params[current].useAttributes && entities[params[current].propertySource]) {
            processedValue = params[current].useAttributes.reduce(
                (attrAcc, attrCurrent) =>
                    attrAcc || entities[params[current].propertySource].$snapshot[attrCurrent],
                '',
            );
        }
        if (params[current].useAttribute && entities[params[current].propertySource]) {
            processedValue =
                entities[params[current].propertySource].$snapshot[params[current].useAttribute];
        }
        if (params[current].pipe) {
            processedValue = tools[params[current].pipe].transform(
                processedValue,
                params[current].dateFormat,
            );
        }

        return {
            ...acc,
            [current]: processedValue,
        };
    }, {});
};

export const getDeepValue = (squareField, sourceObject) => {
    const parts = squareField.split('.');
    const first = parts.shift();

    return parts.length > 0
        ? getDeepValue(parts.join('.'), sourceObject[first])
        : sourceObject[first];
};

export const setBodyClass = (className: string) => {
    const bodyTag = document.body;
    bodyTag.classList.add(className);
};

export const removeBodyClass = (className: string) => {
    const bodyTag = document.body;
    bodyTag.classList.remove(className);
};

let loadScriptCount = 0;

export function loadScriptByUrl(
    url: string,
    afterload: (dynamicScript: HTMLScriptElement, elementId: string) => void,
) {
    let dynamicScript = document.createElement('script');
    dynamicScript.type = 'text/javascript';
    dynamicScript.async = true;
    dynamicScript.src = url;
    dynamicScript.id = 'dynamic_' + loadScriptCount;
    document.head.appendChild(dynamicScript);
    loadScriptCount++;

    dynamicScript.addEventListener('load', () => {
        afterload(dynamicScript, dynamicScript.id);
    });
}

export function loadStyle(libUrl: string, cssFileName: string) {
    const style = document.createElement('link');
    style.id = 'client-theme';
    style.rel = 'stylesheet';
    style.type = 'text/css';
    style.href = `${libUrl}${cssFileName}`;
    document.head.appendChild(style);
}
